<template>
	<VBtn
		:id="id"
		background-color="transparent"
		class="btn--icon"
		elevation="0"
		height="auto"
		icon
		plain
		:ripple="false"
		tile
		width="auto"
		v-bind="$attrs"
		v-on="$listeners"
		@mouseenter="hover = true"
		@mouseleave="hover = false"
	>
		<span
			v-if="!hasSlot"
			class="d-sr-only"
		>
			{{ btnLabel }}
		</span>

		<slot name="prepend" />

		<span
			v-if="badge"
			class="amelipro-btn__badge__wrapper"
		>
			<AmeliproIcon
				ref="ameliproIcon"
				v-bind="$attrs"
				:border-color="iconBorderStyle"
				:bordered="bordered"
				class="icon-custom"
				:class="iconSpacing"
				:icon="currentIconName"
				:icon-bg-color="currentIconBgColor"
				:icon-color="currentIconColor"
				mdi-padding="0"
			>
				<slot name="icon" />
			</AmeliproIcon>

			<span
				class="amelipro-btn__badge"
				:style="badgeStyle"
			>
				<span v-if="isTextBadge">
					{{ badge }}
				</span>
			</span>
		</span>

		<AmeliproIcon
			v-else
			ref="ameliproIcon"
			v-bind="$attrs"
			:border-color="iconBorderStyle"
			:bordered="bordered"
			class="icon-custom"
			:class="iconSpacing"
			:icon="currentIconName"
			:icon-bg-color="currentIconBgColor"
			:icon-color="currentIconColor"
			mdi-padding="0"
		>
			<slot name="icon" />
		</AmeliproIcon>

		<slot name="append" />
	</VBtn>
</template>

<script setup lang="ts">
	import { computed, ref, useAttrs, useSlots } from 'vue';
	import AmeliproIcon from '../AmeliproIcon';
	import { IndexedObject } from '../../types';
	import { VBtn } from 'vuetify/lib';
	import { convertToHex } from '../../functions/convertToHex';
	import { useError } from '../../composables/useError';

	const props = defineProps({
		badge: {
			type: [Boolean, Number, String],
			default: false,
		},
		badgeBgColor: {
			type: String,
			default: undefined,
		},
		badgeColor: {
			type: String,
			default: undefined,
		},
		bordered: {
			type: Boolean,
			default: false,
		},
		btnLabel: {
			type: String,
			default: undefined,
		},
		icon: {
			type: String,
			default: undefined,
		},
		iconBgColor: {
			type: String,
			default: 'transparent',
		},
		iconBorderColor: {
			type: String,
			default: undefined,
		},
		iconColor: {
			type: String,
			required: true,
		},
		iconHoverBgColor: {
			type: String,
			default: 'transparent',
		},
		iconHoverBorderColor: {
			type: String,
			default: undefined,
		},
		iconHoverColor: {
			type: String,
			required: true,
		},
		id: {
			type: String,
			default: undefined,
		},
	});

	const hover = ref(false);

	const currentIconColor = computed<string>(() => {
		if (hover.value) {
			return props.iconHoverColor;
		}
		return props.iconColor;
	});

	const currentIconBgColor = computed<string>(() => {
		if (hover.value) {
			return props.iconHoverBgColor;
		}
		return props.iconBgColor;
	});

	const attrs = useAttrs();
	const currentIconName = computed<string>(() => props.icon ?? attrs?.icon);
	const isTextBadge = computed<boolean>(() => (Boolean(props.badge !== true && props.badge !== false)));
	const badgeStyle = computed<IndexedObject>(() => ({ backgroundColor: convertToHex(props.badgeBgColor), color: convertToHex(props.badgeColor) }));

	const iconBorderStyle = computed<string>(() => {
		if (hover.value) {
			return props.iconHoverBorderColor;
		}

		return props.iconBorderColor;
	});

	const slots = useSlots();
	const hasPrepend = (): boolean => Boolean(slots.prepend);
	const hasAppend = (): boolean => Boolean(slots.append);

	const hasSlot = computed<boolean>(() => {
		const isValid = hasPrepend() || hasAppend();

		if (!isValid && props.btnLabel === undefined) {
			const { propError } = useError();
			propError('prop btnLabel is required if slots are empty');
		}

		return isValid;
	});

	const iconSpacing = computed<string>(() => {
		let spacing = '';

		if (hasPrepend()) {
			spacing += ' ml-2';
		}

		if (hasAppend()) {
			spacing += ' mr-2';
		}

		return spacing;
	});

</script>

<style lang="scss" scoped>
.v-btn {
	letter-spacing: unset;
	text-indent: unset;
}

.v-btn :deep(.v-btn__content) {
	opacity: 1 !important;
}

.v-btn {
	&:focus {
		outline: 1px dotted $ap-black;
	}
}

.amelipro-btn__badge__wrapper {
	position: relative;
}

.amelipro-btn__badge {
	position: absolute;
	top: -0.4rem;
	right: -0.4rem;
	min-width: 1.25rem;
	min-height: 1.25rem;
	padding: 0 0.125rem;
	border-radius: 50%;
	font-size: $font-size-xxs;

	& span {
		display: block;
		margin-top: 2px;
	}
}
</style>
